const initialState = {};

function makeUniqueIds(collection) {
    const used = new Set();

    return collection.map(item => {
        let id = item.id;
        while (used.has(id)) id += 1;
        used.add(id);
        return { ...item, id };
    });
}

function reducer(state = initialState, action) {
    if (action.type === 'LOGOUT_REQUEST' || action.type === 'AUTHENTICATE_REQUEST') {
        return initialState;
    }
    if (action.type === 'APM_CAMERAS_SUCCESS') {
        const { apmId } = action.actionParams;
        const item = state[apmId];

        if (action.payload?.collection && action.payload.collection.length > 1) {
            // this is used to ensure that all cameras have unique channel ID,
            // as backend does not have channel IDs for VAPIX cameras
            // and front-end groups cameras by channel ID.
            // without this, 2 VAPIX-type cameras list one twice
            action.payload.collection = makeUniqueIds(action.payload.collection);
        }

        return {
            ...state,
            [apmId]: {
                ...item,
                ...action.payload,
                collection: {
                    ...(item && item.collection),
                    ...action.payload.collection.reduce(
                        (acc, curr) => ({ ...acc, [curr.id]: curr }),
                        {}
                    ),
                },
                ids: action.payload.collection.map(({ id }) => id),
            },
        };
    }

    if (action.type === 'APM_CAMERAS_FAILURE') {
        const { apmId } = action.actionParams;
        return {
            ...state,
            [apmId]: {
                collection: {},
                ids: [],
            },
        };
    }

    if (action.type === 'SET_APM_CAMERA_STREAM') {
        const { apmId, apmCameraId } = action.payload;
        const item = state[action.payload.apmId];
        return {
            ...state,
            [apmId]: {
                ...item,
                collection: {
                    ...item.collection,
                    [apmCameraId]: {
                        ...item.collection[apmCameraId],
                        streamData: action.payload,
                    },
                },
            },
        };
    }

    if (action.type === 'APM_CAMERA_RECORDINGS_SUCCESS') {
        const { apmId, apmCameraId } = action.actionParams;
        const item = state[apmId];
        const collection = item && item.collection;
        const apmCamera = collection && collection[apmCameraId];
        return {
            ...state,
            [apmId]: {
                ...item,
                collection: {
                    ...collection,
                    [apmCameraId]: {
                        ...apmCamera,
                        recordings: {
                            ...(apmCamera && apmCamera.recordings),
                            ...action.payload,
                            collection: {
                                ...(apmCamera &&
                                    apmCamera.recordings &&
                                    apmCamera.recordings.collection),
                                ...action.payload.collection.reduce(
                                    (acc, curr) => ({ ...acc, [curr.id]: curr }),
                                    {}
                                ),
                            },
                            ids: action.payload.collection.map(({ id }) => id),
                        },
                    },
                },
            },
        };
    }

    if (action.type === 'APM_CAMERA_STREAM_REQUEST') {
        const { apmId, apmCameraId } = action.actionParams;
        const item = state[apmId];
        const collection = item && item.collection;
        return {
            ...state,
            [apmId]: {
                ...item,
                collection: {
                    ...collection,
                    [apmCameraId]: {
                        ...collection[apmCameraId],
                        busy: true,
                        currentStream: {
                            loading: true,
                        },
                    },
                },
            },
        };
    }

    if (action.type === 'APM_CAMERA_STREAM_SUCCESS') {
        const { apmId, apmCameraId, recordingId } = action.actionParams;
        const item = state[apmId];
        const collection = item && item.collection;
        return {
            ...state,
            [apmId]: {
                ...item,
                collection: {
                    ...collection,
                    [apmCameraId]: {
                        ...collection[apmCameraId],
                        busy: false,
                        currentStream: {
                            ...action.payload,
                            recordingId,
                        },
                    },
                },
            },
        };
    }

    if (action.type === 'APM_CAMERA_STREAM_FAILURE') {
        const { apmId, apmCameraId } = action.actionParams;
        const item = state[apmId];
        const collection = item && item.collection;
        return {
            ...state,
            [apmId]: {
                ...item,
                collection: {
                    ...collection,
                    [apmCameraId]: {
                        ...collection[apmCameraId],
                        busy: false,
                        currentStream: {
                            error: action.payload,
                        },
                    },
                },
            },
        };
    }

    if (action.type === 'APM_CAMERA_STREAM_CLOSE') {
        const { apmId, apmCameraId } = action.actionParams;
        const item = state[apmId];
        const collection = item && item.collection;
        return {
            ...state,
            [apmId]: {
                ...item,
                collection: {
                    ...collection,
                    [apmCameraId]: {
                        ...collection[apmCameraId],
                        busy: false,
                        currentStream: {
                            error: action.payload,
                        },
                    },
                },
            },
        };
    }

    if (action.type === 'APM_CAMERA_RECORDING_DOWNLOAD_REQUEST') {
        const { apmId, apmCameraId, id } = action.actionParams;
        const item = state[apmId];
        const collection = item && item.collection;
        const apmCamera = collection && collection[apmCameraId];
        return {
            ...state,
            [apmId]: {
                ...item,
                collection: {
                    ...collection,
                    [apmCameraId]: {
                        ...apmCamera,
                        busy: true,
                        recordings: {
                            ...(apmCamera && apmCamera.recordings),
                            collection: {
                                ...(apmCamera &&
                                    apmCamera.recordings &&
                                    apmCamera.recordings.collection),
                                [id]: {
                                    ...(apmCamera &&
                                        apmCamera.recordings &&
                                        apmCamera.recordings.collection &&
                                        apmCamera.recordings.collection[id]),
                                    downloading: -1,
                                },
                            },
                        },
                    },
                },
            },
        };
    }

    if (
        action.type === 'APM_CAMERA_RECORDING_DOWNLOAD_FINISHED' ||
        action.type === 'APM_CAMERA_RECORDING_DOWNLOAD_FAILURE'
    ) {
        const { apmId, apmCameraId, id } = action.actionParams;
        const item = state[apmId];
        const collection = item && item.collection;
        const apmCamera = collection && collection[apmCameraId];
        return {
            ...state,
            [apmId]: {
                ...item,
                collection: {
                    ...collection,
                    [apmCameraId]: {
                        ...apmCamera,
                        busy: false,
                        recordings: {
                            ...(apmCamera && apmCamera.recordings),
                            collection: {
                                ...(apmCamera &&
                                    apmCamera.recordings &&
                                    apmCamera.recordings.collection),
                                [id]: {
                                    ...(apmCamera &&
                                        apmCamera.recordings &&
                                        apmCamera.recordings.collection &&
                                        apmCamera.recordings.collection[id]),
                                    downloading: false,
                                },
                            },
                        },
                    },
                },
            },
        };
    }

    if (action.type === 'APM_CAMERA_RECORDING_DOWNLOAD_PROGRESS') {
        const { apmId, apmCameraId, id } = action.actionParams;
        const item = state[apmId];
        const collection = item && item.collection;
        const apmCamera = collection && collection[apmCameraId];
        return {
            ...state,
            [apmId]: {
                ...item,
                collection: {
                    ...collection,
                    [apmCameraId]: {
                        ...apmCamera,
                        busy: false,
                        recordings: {
                            ...(apmCamera && apmCamera.recordings),
                            collection: {
                                ...(apmCamera &&
                                    apmCamera.recordings &&
                                    apmCamera.recordings.collection),
                                [id]: {
                                    ...(apmCamera &&
                                        apmCamera.recordings &&
                                        apmCamera.recordings.collection &&
                                        apmCamera.recordings.collection[id]),
                                    downloading: action.payload.value,
                                },
                            },
                        },
                    },
                },
            },
        };
    }

    return state;
}

export default reducer;
